/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * useModuleQuery — generic data hook for any module.
 *
 * Usage (tenant context — orgId comes from repository context):
 *   const { data: riders, loading, save, remove } = useModuleQuery<Rider>('rider-manager');
 *
 * Usage (explicit org — God Mode inspection):
 *   const { data: riders, loading } = useModuleQuery<Rider>('rider-manager', 'org1');
 *
 * The hook is agnostic about whether the underlying repository is
 * LocalStorage or a remote API — the data layer is determined solely
 * by REPO_TYPE in src/config/repository.ts.
 */

import { useState, useEffect, useCallback } from 'react';
import { getRepository } from '../config/repository';
import { isRemoteMode } from '../config/repository';
import { getAuthToken, hasApiStatus } from '../data/ApiModuleRepository';

/**
 * Map of moduleId → route prefix.
 * When a PULSE_UPDATE event fires, we only auto-refresh if the browser
 * is currently on the module's page.  This prevents background data
 * churn (and noisy console logs) when the user is on Dashboard, SaaS
 * Admin, or any other unrelated page.
 *
 * Modules NOT in this map will always respond to PULSE_UPDATE (safe default).
 */
const MODULE_ROUTE_PREFIX: Record<string, string> = {
  'rider-manager': '/apps/rider-manager',
  'rider-ops':     '/apps/rider-manager',
  'aggregator':    '/apps/aggregator',
  'menu-manage':   '/apps/menu-manage',
  'support-desk':  '/apps/support-desk',
};

export function useModuleQuery<T extends { id: string }>(
  moduleId: string,
  orgId?: string,
) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // In remote mode, skip fetch if there is no auth token (user not logged in)
    if (isRemoteMode() && !getAuthToken()) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const items = await getRepository().getAll<T>(moduleId, orgId);
      setData(items);
    } catch (err: any) {
      if (!hasApiStatus(err, 401, 403)) {
        console.error(`[useModuleQuery] Failed to load ${moduleId}`, err);
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [moduleId, orgId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const handlePulse = () => {
      // ── URL-driven gate: only refresh when the user is on this module's page ──
      const prefix = MODULE_ROUTE_PREFIX[moduleId];
      if (prefix && !window.location.pathname.startsWith(prefix)) return;

      if (import.meta.env.DEV) {
        console.log('💓 Heartbeat: Data Refreshed (' + moduleId + ')');
      }
      void refresh();
    };
    window.addEventListener('PULSE_UPDATE', handlePulse);
    return () => window.removeEventListener('PULSE_UPDATE', handlePulse);
  }, [refresh, moduleId]);

  /**
   * Upsert an item.  Returns the saved item (with orgId stamped).
   * Automatically calls refresh() so the hook's data stays current.
   */
  const save = async (item: T): Promise<T> => {
    const saved = await getRepository().save<T>(moduleId, orgId ?? '', item);
    await refresh();
    return saved;
  };

  /**
   * Partial update.  Merges `partial` into the existing item.
   * Automatically calls refresh().
   */
  const update = async (id: string, partial: Partial<T>): Promise<T> => {
    const updated = await getRepository().update<T>(moduleId, orgId ?? '', id, partial);
    await refresh();
    return updated;
  };

  /**
   * Delete item by id.
   * Automatically calls refresh().
   */
  const remove = async (id: string): Promise<void> => {
    await getRepository().delete(moduleId, orgId ?? '', id);
    await refresh();
  };

  return { data, loading, refresh, save, update, remove };
}
